import { Modal, Stack, Text } from '@mantine/core';
import { GoogleLogin } from '@react-oauth/google';
import { useState } from 'react';

/**
 * Shown when the session dies with the app open.
 *
 * Deliberately a modal over the current page rather than a redirect to the sign-in
 * screen: someone half way through building an offer would otherwise lose everything
 * they had typed, having done nothing wrong. Signing in here puts them straight back
 * where they were, with the page untouched.
 *
 * It cannot be dismissed, because behind it every action would fail.
 */
export function SessionExpiredModal({
  onSignIn,
}: {
  onSignIn: (credential: string) => Promise<void>;
}) {
  const [failed, setFailed] = useState(false);

  return (
    <Modal
      opened
      // Not dismissible: behind it every action would fail.
      onClose={() => undefined}
      withCloseButton={false}
      closeOnEscape={false}
      closeOnClickOutside={false}
      centered
      title="Your session has expired"
      zIndex={10000}
    >
      <Stack gap="md">
        <Text size="sm">
          You have been signed out. Sign in again to carry on — this page and
          anything you have typed will still be here.
        </Text>

        <GoogleLogin
          onSuccess={(res) => {
            setFailed(false);
            onSignIn(res.credential!).catch(() => setFailed(true));
          }}
          onError={() => setFailed(true)}
        />

        {failed && (
          <Text size="sm" c="red">
            That did not work. Try again, or reload the page to sign in from the
            start.
          </Text>
        )}
      </Stack>
    </Modal>
  );
}
